<script setup lang="ts">
const { t, locale } = useI18n();
const localePath = useLocalePath();
const switchLocalePath = useSwitchLocalePath();

useSeoMeta({
  title: () => t("legal.charte.seo.titre"),
  description: () => t("legal.charte.seo.description"),
  ogTitle: () => t("legal.charte.seo.ogTitre"),
  ogDescription: () => t("legal.charte.seo.ogDescription"),
  twitterCard: "summary_large_image",
  robots: "index, follow",
});

useHead({
  link: [{ rel: "canonical", href: "https://donoor.org/charte-ethique" }],
});
</script>

<template>
  <div class="min-h-screen">
    <UContainer class="pt-32 pb-20 max-w-4xl">
      <div class="text-gray-700 max-w-none">
        <h1 class="text-4xl font-bold text-gray-900 mb-6">{{ t('legal.charte.titre') }}</h1>
        <div
          v-if="locale !== 'fr'"
          class="mb-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
        >
          {{ t('legal.avisTraduction.texte') }}
          <NuxtLink :to="switchLocalePath('fr')" class="underline font-medium">{{ t('legal.avisTraduction.lienFr') }}</NuxtLink>
        </div>
        <!-- Date FIGÉE, jamais `new Date()` : une charte qui affiche la date du
             jour à chaque visite ne prouve rien de sa version. À mettre à jour
             manuellement lors d'une modification de fond. -->
        <p class="text-gray-600">{{ t('legal.charte.derniereMiseAJour') }}</p>

        <p class="mb-4 mt-8 leading-relaxed">{{ t('legal.charte.intro.texte1') }}</p>
        <p class="mb-4 leading-relaxed">
          <i18n-t keypath="legal.charte.intro.texte2" tag="span">
            <template #lienCgu>
              <NuxtLink :to="localePath({ path: '/terms' })" class="text-primary-600 underline">{{ t('legal.charte.intro.lienCguTexte') }}</NuxtLink>
            </template>
            <template #lienConformite>
              <NuxtLink :to="localePath({ path: '/conformite' })" class="text-primary-600 underline">{{ t('legal.charte.intro.lienConformiteTexte') }}</NuxtLink>
            </template>
          </i18n-t>
        </p>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.intro.texte3') }}</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">
          {{ t('legal.charte.article1.titre') }}
        </h2>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article1.intro') }}</p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>
            <strong>{{ t('legal.charte.article1.items.item1Label') }}</strong>
            {{ t('legal.charte.article1.items.item1TexteAvant') }}<em>{{ t('legal.charte.article1.items.item1TexteEm') }}</em>{{ ' ' }}{{ t('legal.charte.article1.items.item1TexteApres') }}
          </li>
          <li>
            <strong>{{ t('legal.charte.article1.items.item2Label') }}</strong>
            {{ t('legal.charte.article1.items.item2Texte') }}
          </li>
          <li>
            <strong>{{ t('legal.charte.article1.items.item3Label') }}</strong>
            {{ t('legal.charte.article1.items.item3Texte') }}
          </li>
          <li>
            <strong>{{ t('legal.charte.article1.items.item4Label') }}</strong>
            {{ t('legal.charte.article1.items.item4Texte') }}
          </li>
        </ul>

        <h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">
          {{ t('legal.charte.article2.titre') }}
        </h2>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article2.intro') }}</p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>{{ t('legal.charte.article2.items.item1') }}</li>
          <li>{{ t('legal.charte.article2.items.item2') }}</li>
          <li>{{ t('legal.charte.article2.items.item3') }}</li>
          <li>{{ t('legal.charte.article2.items.item4') }}</li>
          <li>{{ t('legal.charte.article2.items.item5') }}</li>
          <li>{{ t('legal.charte.article2.items.item6') }}</li>
          <li>{{ t('legal.charte.article2.items.item7') }}</li>
          <li>{{ t('legal.charte.article2.items.item8') }}</li>
          <li>{{ t('legal.charte.article2.items.item9') }}</li>
          <li>{{ t('legal.charte.article2.items.item10') }}</li>
        </ul>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article2.closing') }}</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">
          {{ t('legal.charte.article3.titre') }}
        </h2>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article3.texte1') }}</p>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article3.texte2') }}</p>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article3.texte3') }}</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">
          {{ t('legal.charte.article4.titre') }}
        </h2>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article4.intro') }}</p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>{{ t('legal.charte.article4.items.item1') }}</li>
          <li>{{ t('legal.charte.article4.items.item2') }}</li>
          <li>{{ t('legal.charte.article4.items.item3') }}</li>
        </ul>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article4.closing') }}</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">
          {{ t('legal.charte.article5.titre') }}
        </h2>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article5.texte1') }}</p>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article5.texte2') }}</p>
        <p class="mb-4 leading-relaxed">
          <strong>{{ t('legal.charte.article5.texte3Gras') }}</strong>
          {{ t('legal.charte.article5.texte3Apres') }}
        </p>

        <h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">
          {{ t('legal.charte.article6.titre') }}
        </h2>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article6.texte') }}</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">
          {{ t('legal.charte.article7.titre') }}
        </h2>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article7.texte1') }}</p>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article7.texte2') }}</p>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article7.texte3') }}</p>

        <h2 class="text-2xl font-bold text-gray-900 mt-8 mb-4">
          {{ t('legal.charte.article8.titre') }}
        </h2>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article8.texte1') }}</p>
        <p class="mb-4 leading-relaxed">{{ t('legal.charte.article8.texte2') }}</p>
        <ul class="list-disc pl-6 mb-4 space-y-2">
          <li>{{ t('legal.contactListe.email') }}</li>
          <li>{{ t('legal.contactListe.adresse') }}</li>
        </ul>
      </div>
    </UContainer>
  </div>
</template>
